import React, { useState, useRef, useEffect, JSX } from 'react';
import { CtaButton, MiniButton, Modal } from '@greystar/designsystem';
import {
  clampTotalEntries,
  getWindowLocation,
  isBrowser,
  useFacetGenerator,
  usePagination,
} from '@greystar/lib';
import RegularFacet, { ClearRegularFacetRef } from './regular-filter';
import NumericFacet, { ClearFacetRef } from './numeric-range-filer';
import { RegularFacetState, NumericFacetState } from '@coveo/headless-react/ssr-commerce';
import { BaseProductWithPriceOptions } from '@greystar/ui/src/component-props';
import { useProductList, useAnalytics, getDictionaryEntry } from '@greystar/lib';
import { useI18n } from 'next-localization';

interface EventMapItem {
  event: string;
  action_type: string;
  action_value?: string;
  price_range_filter?: string;
  property_type_filter?: string;
  property_features_filter?: string;
  timeline_filter?: string;
  click_text?: string;
}

interface SearchFilterProps {
  resultCount: number | null;
  setActiveMapEvent?: (value: boolean) => void;
  setPaginationStatus?: (value: boolean) => void;
  isLoading: boolean;
  onApplyFilters: (
    filteredProperties: BaseProductWithPriceOptions[],
    facetSelections: Record<string, Set<string>>,
    numericSelections: Record<string, NumericRange>
  ) => void;
  onClearFilters: (filteredProperties?: BaseProductWithPriceOptions[]) => void;
  onCloseModal?: () => void;
  appliedProperties: BaseProductWithPriceOptions[];
  data?: {
    state?: string;
    location?: string;
    name?: string;
    searchTerm?: string;
  };
  isMapSearch?: boolean;
  isFilterApplied?: boolean;
}

export interface NumericRange {
  start: number;
  end: number;
}

const SearchFilter = ({
  resultCount,
  isLoading,
  onApplyFilters,
  onClearFilters,
  setActiveMapEvent,
  setPaginationStatus,
  appliedProperties,
  data,
  isFilterApplied,
}: SearchFilterProps): JSX.Element => {
  const { t } = useI18n();
  const { state, methods } = useFacetGenerator();
  const { state: paginateState } = usePagination();
  const { state: productListState } = useProductList();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [facetSelections, setFacetSelections] = useState<Record<string, Set<string>>>({});
  const [appliedFacetSelections, setAppliedFacetSelections] = useState<Record<string, Set<string>>>(
    {}
  );
  const analytics = useAnalytics();
  const [numericSelections, setNumericSelections] = useState<Record<string, NumericRange>>({});
  const [appliedNumericSelections, setAppliedNumericSelections] = useState<
    Record<string, NumericRange>
  >({});

  const trackFilterToggle = (filterType: string, isExpanded: boolean) => {
    const action = isExpanded ? 'show' : 'hide';

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: `filters_${action}`,
      action_type: `${filterType} - ${action}`,
      state: appliedProperties[0]?.additionalFields?.state ?? '',
      property_location: (appliedProperties[0]?.additionalFields?.city as string)?.trim() ?? '',
      property_name: '',
      module_name: 'search filters',
      inpage_location: 'middle',
      click_text: `${action}`,
      click_type: 'internal',
      destination_url: window.location.href,
      search_term: data?.searchTerm || '',
    });
  };

  const clearNumericFacetRef = useRef<Record<string, ClearFacetRef | null>>({});
  const clearRegularFacetRef = useRef<Record<string, ClearRegularFacetRef | null>>({});

  const [urlCountParam, setUrlCountParam] = useState(0);

  useEffect(() => {
    if (!isBrowser) return;

    const url = new URL(window.location.href);
    const count = parseInt(url.searchParams.get('fcount') || '0', 10);
    if (!isNaN(count)) setUrlCountParam(count);

    // Sync facetSelections after reload (filters)
    const initialFacetSelections: Record<string, Set<string>> = {};
    const initialNumericSelections: Record<string, NumericRange> = {};

    state.forEach((facetState: { facetId: string; type: string }) => {
      const facetId = facetState.facetId;

      if (facetState.type === 'regular') {
        const controller = methods?.getFacetController(facetId, 'regular');
        if (controller) {
          const selectedValues = controller.state.values
            .filter((v: { state: string }) => v.state !== 'idle')
            .map((v: { value: string }) => v.value);
          if (selectedValues.length > 0) {
            initialFacetSelections[facetId] = new Set(selectedValues);
          }
        }
      }

      if (facetState.type === 'numericalRange') {
        const controller = methods?.getFacetController(facetId, 'numericalRange');
        if (controller) {
          const selectedRange = controller.state.values.find(
            (v: { state: string }) => v.state === 'selected'
          );
          if (selectedRange) {
            initialNumericSelections[facetId] = {
              start: selectedRange.start,
              end: selectedRange.end,
            };
          }
        }
      }
    });
    setAppliedFacetSelections(initialFacetSelections);
    setAppliedNumericSelections(initialNumericSelections);

    // Update URL with fcount if missing
    const facetCount =
      Object.keys(initialFacetSelections).length + Object.keys(initialNumericSelections).length;
    if (!url.searchParams.has('fcount') && facetCount > 0) {
      url.searchParams.set('fcount', facetCount.toString());
      window.history.replaceState({}, '', url.toString());
      setUrlCountParam(facetCount);
    }
  }, [state]);

  const facetOrder = [
    'ec_price',
    'bedrooms_string',
    'bathrooms_string',
    'building_type',
    'highlights',
  ];

  const appliedCount =
    Object.values(appliedFacetSelections).length + Object.keys(appliedNumericSelections).length;

  const selectedFilterCount = urlCountParam ?? appliedCount;

  const trackFilterEvent = (
    eventType: string,
    filterType: string,
    action: string,
    actionValue?: string,
    additionalData: Record<string, string> = {}
  ) => {
    const baseEventData = {
      state: appliedProperties[0]?.additionalFields?.state ?? '',
      property_location: (appliedProperties[0]?.additionalFields?.city as string)?.trim() ?? '',
      property_name: '',
      module_name: 'search filters',
      inpage_location: 'middle',
      click_type: 'internal',
      destination_url: getWindowLocation(),
      search_term: data?.searchTerm || '',
    };

    const eventMap: Record<string, EventMapItem> = {
      close: {
        event: 'filters_close',
        action_type: `${filterType} filters - close`,
      },
      open: {
        event: 'filters_open',
        action_type: 'all filters - open',
      },
      clear: {
        event: 'filters_clear',
        action_type: `${filterType} filters - clear`,
      },
      toggle: {
        event: `filters_${action}`,
        action_type: `${filterType} filters - ${action}`,
      },
      selection: {
        event: 'filters_selection',
        action_type: `${filterType} - ${action}`,
        action_value: actionValue || '',
      },
      results: {
        event: 'filters_results',
        action_type: `${filterType} filters - show results`,
        price_range_filter: additionalData.priceRange || '',
        property_type_filter: additionalData.propertyType || '',
        property_features_filter: additionalData.propertyFeatures || '',
        timeline_filter: additionalData.timeline || '',
        click_text: `Show ${resultCount} results`,
      },
    };

    analytics.trackEvent(eventMap[eventType].event, {
      ...baseEventData,
      ...eventMap[eventType],
      ...additionalData,
    });
  };

  const getFilterType = (facetId: string): string => {
    const filterTypes: Record<string, string> = {
      ec_price: 'budget',
      bedrooms_string: 'bedrooms',
      bathrooms_string: 'bathrooms',
      building_type: 'building type',
      highlights: 'features',
    };
    return filterTypes[facetId] || facetId;
  };

  function getBedroomLabel(value: string | number): string {
    const num = Number(value);
    if (num === 0) return 'Studio';
    return `${num} ${num === 1 ? 'Bedroom' : 'Bedrooms'}`;
  }

  function getBathroomLabel(value: string | number): string {
    const num = Number(value);
    if (num === 0) return 'No Bathroom';
    return `${num} ${num === 1 ? 'Bathroom' : 'Bathrooms'}`;
  }

  const getFilterDisplayValue = (facetId: string, value: string): string => {
    if (facetId === 'bedrooms_string') {
      return getBedroomLabel(value);
    }
    if (facetId === 'bathrooms_string') {
      return getBathroomLabel(value);
    }
    if (facetId === 'ec_price') {
      return `$${value}`;
    }
    return value;
  };

  const openFilterModal = () => {
    setIsFilterVisible(true);
    setActiveMapEvent?.(false);
    setPaginationStatus?.(false);
    setFacetSelections(
      Object.fromEntries(
        Object.entries(appliedFacetSelections).map(([key, values]) => [key, new Set(values)])
      )
    );
    setNumericSelections({ ...appliedNumericSelections });

    trackFilterEvent('open', 'all', 'open');
    if (isBrowser) document.body.style.overflow = 'hidden';
  };

  const closeFilterModal = () => {
    showResults();
    trackFilterEvent('close', 'all', 'close');
    setIsFilterVisible(false);
    if (isBrowser) document.body.style.overflow = 'auto';
  };

  const clearFilter = (): void => {
    setUrlCountParam(0);
    setFacetSelections({});
    setNumericSelections({});
    setAppliedFacetSelections({});
    setAppliedNumericSelections({});

    state.forEach((facetState: RegularFacetState | NumericFacetState) => {
      const facetId = facetState.facetId;
      if (facetState.type === 'regular') {
        const controller = methods?.getFacetController(facetId, 'regular');
        controller?.deselectAll();
      }
      if (facetState.type === 'numericalRange') {
        const controller = methods?.getFacetController(facetId, 'numericalRange');
        controller?.deselectAll();
      }
    });
    onClearFilters();
    trackFilterEvent('clear', 'all', 'clear');
    if (isBrowser) {
      const url = new URL(window.location.href);
      url.searchParams.delete('fcount');
      const updatedUrl = `${url.pathname}?${url.searchParams.toString()}`;
      window.history.replaceState({}, '', updatedUrl);
    }
  };

  const showResults = (): void => {
    const formatSelectedValues = (facetId: string, values: Set<string>): string => {
      if (facetId === 'bedrooms_string') {
        return Array.from(values)
          .map((v) => `${v} ${v === '1' ? 'bedroom' : 'bedrooms'}`)
          .join(', ');
      }
      if (facetId === 'bathrooms_string') {
        return Array.from(values)
          .map((v) => `${v} ${v === '1' ? 'bathroom' : 'bathrooms'}`)
          .join(', ');
      }
      return Array.from(values).join(', ');
    };

    // Get property type filters
    const propertyTypeParts = [];

    if (facetSelections['bedrooms_string']?.size) {
      propertyTypeParts.push(
        formatSelectedValues('bedrooms_string', facetSelections['bedrooms_string'])
      );
    }

    if (facetSelections['bathrooms_string']?.size) {
      propertyTypeParts.push(
        formatSelectedValues('bathrooms_string', facetSelections['bathrooms_string'])
      );
    }

    if (facetSelections['building_type']?.size) {
      propertyTypeParts.push(
        formatSelectedValues('building_type', facetSelections['building_type'])
      );
    }

    const propertyTypeFilterValue = propertyTypeParts.join('|');

    // Get property features filters
    const propertyFeaturesFilterValue = facetSelections['highlights']
      ? formatSelectedValues('highlights', facetSelections['highlights'])
      : undefined;

    const priceRange = `${numericSelections['ec_price']?.start || ''}-${
      numericSelections['ec_price']?.end || ''
    }`;
    trackFilterEvent('results', 'all', 'show results', undefined, {
      property_type_filter: propertyTypeFilterValue ?? '',
      property_features_filter: propertyFeaturesFilterValue ?? '',
      price_range_filter: priceRange ?? '',
    });

    if (!isLoading) {
      setAppliedFacetSelections(
        Object.fromEntries(
          Object.entries(facetSelections).map(([key, values]) => [key, new Set(values)])
        )
      );
      setAppliedNumericSelections({ ...numericSelections });
      const filteredIds = productListState.products.map(
        (p: { permanentid: number }) => p.permanentid
      );
      const filteredProperties = appliedProperties.filter((p) =>
        filteredIds.includes(p.permanentid)
      );
      onApplyFilters(filteredProperties, facetSelections, numericSelections);
      setIsFilterVisible(false);
      if (isBrowser) {
        document.body.style.overflow = 'auto';
        const url = new URL(window.location.href);
        const facetCount =
          Object.values(appliedFacetSelections).length +
          Object.keys(appliedNumericSelections).length;

        setUrlCountParam(facetCount); // force update UI state

        if (facetCount > 0) {
          url.searchParams.set('fcount', facetCount.toString());
        } else {
          url.searchParams.delete('fcount');
        }

        window.history.replaceState({}, '', url.toString());
      }
    }
  };

  const totalEntries: number = clampTotalEntries(
    paginateState.totalPages,
    paginateState.pageSize,
    isFilterApplied ? resultCount : paginateState.totalEntries
  );

  const getCtaState = () => {
    if (isLoading) {
      return { label: 'Loading...', disabled: true, onClick: undefined };
    }

    return {
      label:
        totalEntries !== null
          ? totalEntries === 0
            ? getDictionaryEntry(t('searchfilter_noresult'), 'No results')
            : `Show ${totalEntries} result${totalEntries === 1 ? '' : 's'}`
          : getDictionaryEntry(t('searchfilter_showresults'), 'Show results'),
      disabled: false,
      onClick: showResults,
    };
  };

  const cta = getCtaState();

  const sortedFacets = [...state].sort((a, b) => {
    const aIndex = facetOrder.indexOf(a.facetId);
    const bIndex = facetOrder.indexOf(b.facetId);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return 0;
  });

  useEffect(() => {
    const handleFilterButtonClick = (event: MouseEvent) => {
      const button = event.target as HTMLElement;
      const filterButton = button.closest('.mini-cta--button');

      if (filterButton) {
        // Find the parent filter section
        const filterSection = filterButton.closest('.filter-group');
        if (!filterSection) return;
        const filterType =
          filterSection.querySelector('.filter-group--header--title')?.textContent ?? '';

        // Determine if this is show or hide action
        const labelElement = filterButton.querySelector('.mini-cta--label');
        const action = labelElement?.textContent?.toLowerCase().trim() === 'hide' ? 'show' : 'hide';
        if (filterType != '') {
          trackFilterToggle(filterType, action === 'show');
        }
      }
    };

    document.addEventListener('click', handleFilterButtonClick);
    return () => document.removeEventListener('click', handleFilterButtonClick);
  }, []);

  return (
    <div className="search-filter">
      <MiniButton
        variant="secondary"
        label={`All filters${selectedFilterCount > 0 ? ` (${selectedFilterCount})` : ''}`}
        onClick={openFilterModal}
        icon={{ name: 'Filter', position: 'right' }}
      />
      {isFilterVisible && (
        <Modal>
          <Modal.Header onClose={closeFilterModal}>
            <div className="filters-modal--title-wrapper">
              <p className="filters-modal--title-wrapper--title">Filters</p>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="filters-modal--body-wrapper">
              {sortedFacets.map((facetState: RegularFacetState | NumericFacetState) => {
                const facetId = facetState.facetId;
                switch (facetState.type) {
                  case 'regular': {
                    if (!clearRegularFacetRef.current[facetId]) {
                      clearRegularFacetRef.current[facetId] = null;
                    }
                    const controller = methods?.getFacetController(facetId, 'regular');
                    return (
                      <RegularFacet
                        ref={(ref) => {
                          clearRegularFacetRef.current[facetId] = ref;
                        }}
                        key={facetId}
                        controller={controller}
                        staticState={facetState}
                        selections={facetSelections[facetId] ?? new Set<string>()}
                        onSelectionChange={(value) => {
                          if (!controller) return;
                          const facetValues = controller.state.values;
                          const target = facetValues.find(
                            (v: { value: string }) => v.value === value
                          );
                          if (target) {
                            controller.toggleSelect(target);
                            const isSelected = facetSelections[facetId]?.has(value);
                            trackFilterEvent(
                              'selection',
                              getFilterType(facetId),
                              isSelected ? 'deselected' : 'selected',
                              getFilterDisplayValue(facetId, value)
                            );
                            setFacetSelections((prev) => {
                              const currentValues = prev[facetId] ?? new Set<string>();
                              const newValues = new Set(currentValues);
                              if (newValues.has(value)) {
                                newValues.delete(value);
                              } else {
                                newValues.add(value);
                              }
                              return {
                                ...prev,
                                [facetId]: newValues,
                              };
                            });
                          }
                        }}
                      />
                    );
                  }
                  case 'numericalRange': {
                    if (!clearNumericFacetRef.current[facetId]) {
                      clearNumericFacetRef.current[facetId] = null;
                    }
                    const controller = methods?.getFacetController(facetId, 'numericalRange');
                    return (
                      <NumericFacet
                        ref={(ref) => {
                          clearNumericFacetRef.current[facetId] = ref;
                        }}
                        key={facetId}
                        controller={controller}
                        staticState={facetState}
                        range={
                          numericSelections[facetId] ?? {
                            start: facetState.domain?.min ?? facetState.values[0]?.start ?? 0,
                            end: facetState.domain?.max ?? facetState.values[0]?.end ?? 0,
                          }
                        }
                        onRangeChange={(range, changed) => {
                          if (!controller) return;
                          controller.setRanges([
                            {
                              start: range.start,
                              end: range.end,
                              endInclusive: true,
                              state: 'selected',
                            },
                          ]);
                          trackFilterEvent(
                            'selection',
                            'budget',
                            changed === 'start' ? 'min price' : 'max price',
                            (changed === 'start' ? range.start : range.end).toString()
                          );
                          setNumericSelections((prev) => ({
                            ...prev,
                            [facetId]: { start: range.start, end: range.end },
                          }));
                        }}
                      />
                    );
                  }
                  default:
                    return null;
                }
              })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="filters-modal--footer-wrapper">
              <div className="results-control--buttons-wrapper">
                <MiniButton onClick={clearFilter} label="Clear Filters" variant="text" />
                <CtaButton
                  variant="primary"
                  label={cta.label}
                  onClick={cta.onClick}
                  isDisabled={cta.disabled}
                />
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default SearchFilter;
